import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  useTrashedPayments,
  useRestorePayments,
  usePermanentlyDeletePayments,
} from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { joinFullName } from '../../utils/patientName';
import { PaymentDetailsModal } from './PaymentDetailsModal';
import type { Payment } from '../../types/billing';
import {
  Loader2,
  AlertCircle,
  Search,
  X,
  RotateCcw,
  Trash2,
  ArrowLeft,
  Eye,
  Receipt,
} from 'lucide-react';

const CURRENCY = '৳';
const LIMIT = 20;

const METHOD_LABELS: Record<string, string> = {
  cash: 'Cash',
  card: 'Card',
  upi: 'UPI',
  bank_transfer: 'Bank Transfer',
  insurance: 'Insurance',
};

function fmt(value: number | undefined | null): string {
  return `${CURRENCY}${(Number(value) || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function patientName(payment: any): string {
  const patient = payment?.patient;
  const name = patient ? joinFullName(patient) : '';
  return name || payment?.patientId?.slice(0, 8) || '—';
}

/**
 * Trash for invoice payments.
 *
 * Moving a payment to trash already removes its amount from every collection
 * total (list sum, Top Sheet, reports). This page only restores the record —
 * which re-posts its journal entry, cashbook line and invoice balance — or
 * deletes it for good.
 */
export function BillingPaymentsTrash() {
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [viewing, setViewing] = useState<Payment | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<{ mode: 'single' | 'bulk'; payment?: any } | null>(
    null,
  );
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, error } = useTrashedPayments(page, LIMIT, debouncedSearch.trim() || undefined);
  const restorePayments = useRestorePayments();
  const permanentDelete = usePermanentlyDeletePayments();

  const payments = data?.data || [];
  const meta = data?.meta;

  useEffect(() => {
    setPage(1);
    setSelectedIds([]);
  }, [debouncedSearch]);

  const allSelected = payments.length > 0 && payments.every((p: any) => selectedIds.includes(p.id));
  const someSelected = selectedIds.length > 0;

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !payments.some((p: any) => p.id === id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...payments.map((p: any) => p.id)])));
    }
  };

  const toggleSelectOne = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleRestore = async (id: string) => {
    try {
      await restorePayments.mutateAsync([id]);
      setSelectedIds((prev) => prev.filter((x) => x !== id));
      toast.success('Payment restored');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to restore payment');
    }
  };

  const handleBulkRestore = async () => {
    try {
      const res: any = await restorePayments.mutateAsync(selectedIds);
      const restored = res?.data?.restored?.length ?? selectedIds.length;
      if (restored === 0) {
        toast.error('No payments could be restored');
      } else {
        toast.success(`Restored ${restored} payment${restored === 1 ? '' : 's'}`);
      }
      setSelectedIds([]);
    } catch {
      toast.error('Failed to restore some payments');
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmTarget) return;
    try {
      if (confirmTarget.mode === 'single') {
        await permanentDelete.mutateAsync([confirmTarget.payment.id]);
        setSelectedIds((prev) => prev.filter((x) => x !== confirmTarget.payment.id));
        toast.success('Payment permanently deleted');
      } else {
        const res: any = await permanentDelete.mutateAsync(selectedIds);
        const deleted = res?.data?.deleted?.length ?? selectedIds.length;
        toast.success(`Permanently deleted ${deleted} payment${deleted === 1 ? '' : 's'}`);
        setSelectedIds([]);
      }
    } catch (err: any) {
      if (confirmTarget.mode === 'bulk') toast.error('Failed to delete some payments');
      else toast.error(err?.message || 'Failed to delete payment');
    } finally {
      setConfirmTarget(null);
    }
  };

  const busy = restorePayments.isPending || permanentDelete.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/billing/payments')}
          className="p-2 rounded-lg hover:bg-background text-text-muted transition-colors cursor-pointer"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-danger/10 rounded-lg">
            <Trash2 className="w-5 h-5 text-danger" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-text">Payments Trash</h1>
            <p className="text-xs text-text-muted">
              Trashed payments are excluded from collections and reports. Restore or delete them permanently.
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-surface rounded-xl border border-border p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by payment no., patient, mobile, or invoice no..."
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
            />
          </div>
          {search && (
            <button
              onClick={() => setSearch('')}
              className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk actions */}
      {someSelected && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.length}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBulkRestore}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-success text-white text-sm font-medium hover:bg-success/90 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {restorePayments.isPending ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
              Restore
            </button>
            <button
              onClick={() => setConfirmTarget({ mode: 'bulk' })}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-danger text-white text-sm font-medium hover:bg-danger/90 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {permanentDelete.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete permanently
            </button>
            <button
              onClick={() => setSelectedIds([])}
              disabled={busy}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load trashed payments</span>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3 w-10 text-left">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      disabled={payments.length === 0}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      title="Select all"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Payment #</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Patient</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Invoice</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Method</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Trashed</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {payments.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-16 text-center">
                      <Receipt className="w-10 h-10 mx-auto mb-3 text-text-muted/40" />
                      <p className="text-sm font-medium text-text-muted mb-1">
                        {search ? 'No trashed payments match your search' : 'Trash is empty'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  payments.map((payment: any) => (
                    <tr key={payment.id} className="hover:bg-background/50 transition-colors">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(payment.id)}
                          onChange={() => toggleSelectOne(payment.id)}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-text">
                        <button
                          type="button"
                          onClick={() => setViewing(payment)}
                          className="hover:text-primary hover:underline transition-colors"
                          title="View payment details"
                        >
                          {payment.paymentNumber}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-sm text-text">{patientName(payment)}</td>
                      <td className="px-4 py-3 text-sm font-mono text-text-muted">
                        {payment.invoice?.invoiceNumber || '—'}
                      </td>
                      <td
                        className={`px-4 py-3 text-sm text-right font-medium ${
                          Number(payment.amount) < 0 ? 'text-danger' : 'text-success'
                        }`}
                      >
                        {fmt(payment.amount)}
                      </td>
                      <td className="px-4 py-3 text-sm text-text">
                        {METHOD_LABELS[payment.method] || payment.method}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-muted">
                        {new Date(payment.paymentDate).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-muted">
                        {payment.deletedAt ? new Date(payment.deletedAt).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setViewing(payment)}
                            disabled={busy}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors disabled:opacity-50"
                            title="View details"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => handleRestore(payment.id)}
                            disabled={busy}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-success transition-colors disabled:opacity-50"
                            title="Restore"
                          >
                            <RotateCcw size={16} />
                          </button>
                          <button
                            onClick={() => setConfirmTarget({ mode: 'single', payment })}
                            disabled={busy}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors disabled:opacity-50"
                            title="Delete permanently"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {meta && (
            <Pagination
              page={meta.page}
              totalPages={meta.totalPages}
              total={meta.total}
              limit={meta.limit}
              onPageChange={setPage}
              itemName="trashed payments"
            />
          )}
        </div>
      )}

      <PaymentDetailsModal
        open={viewing !== null}
        payment={viewing}
        onClose={() => setViewing(null)}
      />

      <ConfirmDialog
        open={confirmTarget !== null}
        title={confirmTarget?.mode === 'bulk' ? 'Delete payments permanently' : 'Delete payment permanently'}
        description={
          confirmTarget?.mode === 'bulk' ? (
            `Permanently delete ${selectedIds.length} selected payment(s)? This cannot be undone.`
          ) : (
            <>
              Permanently delete <strong>{confirmTarget?.payment?.paymentNumber}</strong>? This cannot be
              undone.
            </>
          )
        }
        confirmLabel="Delete permanently"
        loading={busy}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmTarget(null)}
      />
    </div>
  );
}
